import { RUN_STATUS } from '@bugbug/core/constants/status';
import { useCallback, useMemo } from 'react';

import type { Test, TestRun } from '@bugbug/core/types/tests';
import { useAppDispatch } from '~/modules/store';
import { TestRunActions } from '~/modules/testRun/testRun.redux';
import { ACTIONS } from '~/services/toasts/internalServerError';
import { useTestRunnerActionsState } from '~/views/TestDetails/components/TestActions/TestHeaderActions.hooks';

import { testsApi } from '../../tests.api';
import { showTestsError } from '../../tests.utils';

interface StopTestParams {
  testRunId?: TestRun['id'];
}

export const useStopTest = () => {
  const dispatch = useAppDispatch();
  const [stopTest, stopTestState] = testsApi.useStopTestMutation();
  const { setPendingAction, resetPendingAction } = useTestRunnerActionsState();

  const handleStopTest = useCallback(
    async ({ testId, params = {} }: { testId: Test['id']; params?: StopTestParams }) => {
      setPendingAction('stop');

      const result = stopTest({
        testId,
        ...params,
      }).unwrap();

      result
        .then((testRun) => {
          const testRunId = params.testRunId || testRun.id;
          dispatch(TestRunActions.getSingleSuccess(testRun));

          if (testRunId) {
            dispatch(TestRunActions.updateStatusSucceeded(testRunId, RUN_STATUS.STOPPED));
          }
        })
        .catch((error) => {
          showTestsError(ACTIONS.STOP, error);
        })
        .finally(() => {
          resetPendingAction();
        });

      return result;
    },
    [dispatch, resetPendingAction, setPendingAction, stopTest],
  );

  return useMemo(() => [handleStopTest, stopTestState] as const, [handleStopTest, stopTestState]);
};
